import axios, {AxiosPromise, AxiosRequestConfig} from 'axios';
import FileDownload from 'js-file-download';

import {IUrlConfig} from 'entities/metadata';
import {ERequestType} from 'shared/types/requests';
import {store} from 'store/config';

import {isProduction} from './is-production';

export type TRequestPromise<T = any> = AxiosPromise<T>;
export type TRequestConfig = AxiosRequestConfig & {urlConfig?: IUrlConfig};

const defaultBaseUrl = isProduction() ? `${window.location.origin}`
    : `${process.env.REACT_APP_BASE_URL}`;
export const DEFAULT_REQUEST_CONFIG: AxiosRequestConfig = {
    baseURL: `${defaultBaseUrl}/nalmon/api`,
};

export const performRequest = <T = any>(config: TRequestConfig, ignoreMethodCheck = false): TRequestPromise<T> => {
    const requestConfig: AxiosRequestConfig = {
        ...DEFAULT_REQUEST_CONFIG,
        ...config,
    };

    if (config?.urlConfig) {
        const {baseUrl, contextUrl} = config?.urlConfig;
        requestConfig.baseURL = `${baseUrl || defaultBaseUrl}${contextUrl || '/nalmon/api'}`;
        if (store.getState().AUTH_SLICE.microserviceToken) {
            requestConfig.headers = {
                ...requestConfig.headers,
                Authorization: store.getState().AUTH_SLICE.microserviceToken,
            };
        }
    }

    if (config.data && config.method === 'GET' && !ignoreMethodCheck) {
        // TODO: Tell backend to make this request 'POST'
        const overrideConfig: AxiosRequestConfig = {
            ...requestConfig,
            method: 'POST',
        };
        return axios(overrideConfig);
    }

    return (
        axios(requestConfig)
    );
};

export const downloadFile = async (url: string, filename: string, headers: Record<string, string> = {}) => {
    const fileResponse = await performRequest({
        method: 'GET',
        url,
        responseType: 'blob',
        headers,
    });

    FileDownload(fileResponse.data, filename);
    return fileResponse;
};

export const fetchDocumentStatus = (referenceUrl: string, funcName: string, docId: string) => (
    performRequest({
        url: 'documents/check-doc-status',
        method: 'GET',
        params: {
            docId,
            funcName,
        },
    })
);
export const editTableRows = (
    requestType: ERequestType,
    referenceUrl: string,
    itemList: any,
) => (
    performRequest({
        url: referenceUrl,
        method: requestType,
        data: itemList,
    })
);
export const fetchProperty = (propertyCode: string) => (
    performRequest({
        url: `/property/${propertyCode}`,
        method: 'GET',
        params: {
            propertyCode,
        },
    })
);

export const checkDisabledActions = (referenceUrl: string, functionName: string, params?: any) => (
    performRequest({
        url: referenceUrl,
        method: 'GET',
        params: {
            functionName,
            ...params,
        },
    })
);

/**
 * Выполняет асинхронную функцию итератора для каждого элемента массива,
 * но не более чем `concurrency` промисов одновременно.
 *
 * Позволяет контролировать нагрузку на сеть/сервер и избегать «лавины»
 * stalled-запросов, ограничивая количество параллельных операций.
 *
 * @typeParam T - Тип элементов в массиве `items`.
 *
 * @param concurrency - Максимальное количество одновременно выполняемых промисов.
 * Должно быть положительным целым числом.
 * @param items - Массив элементов, для каждого из которых будет вызван `iteratorFn`.
 * @param iteratorFn - Асинхронная функция, принимающая элемент массива и возвращающая Promise.
 *
 * @returns Promise, который разрешается массивом результатов в том же порядке,
 * что и исходные элементы `items`
 *
 * @remarks
 * - Порядок результатов строго соответствует порядку `items`
 * - При возникновении ошибки в одном из промисов выполнение оставшихся не прерывается,
 *   но результирующий Promise отклонится сразу после первой ошибки.
 * - Если нужно обработать все элементы даже при наличии ошибок, оберните `iteratorFn`
 *   в try/catch внутри.
 */
export const asyncPool = async <T, R>(
    concurrency: number,
    items: T[],
    iteratorFn: (item: T) => Promise<R>
): Promise<R[]> => {
    const results: Promise<R>[] = [];
    const queue = new Set<Promise<void>>();

    await items.reduce<Promise<void>>(async (previousPromise, item) => {
        // Ждём завершения предыдущей итерации (освобождения слота)
        await previousPromise;

        const task = Promise.resolve().then(() => iteratorFn(item));

        // Сохраняем задачу в массив результатов
        results.push(task);

        // Создаём трекер — промис, который всегда разрешается (независимо от успеха/ошибки task)
        const tracker = task.then(
            () => {},   // при успехе – просто разрешаем
            () => {}    // при ошибке – всё равно разрешаем, чтобы не блокировать очередь
        );

        // Добавляем трекер в очередь активных задач
        queue.add(tracker);

        // Удаляем трекер из очереди после его завершения
        const clean = () => queue.delete(tracker);
        tracker.then(clean, clean); // оба колбэка сработают при любом исходе трекера

        // Если достигнут лимит параллельности, ждём освобождения любого слота
        if (queue.size >= concurrency) {
            await Promise.race(queue);
        }
    }, Promise.resolve());

    // Возвращаем результаты всех задач
    return Promise.all(results);
};
